/**
 * SLA breach evaluation for inbox highlights (chats + tickets).
 * Human agent = sender "agent" (not bot/system/note).
 */

/**
 * @param {string | null | undefined} iso
 * @returns {number | null}
 */
function parseIso(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * Closed / resolved conversations cannot breach SLA (same rule as tickets).
 * @param {string | null | undefined} status
 */
function isClosedConversationStatus(status) {
  const s = String(status ?? "").trim().toLowerCase();
  return s === "resolved" || s === "closed" || s === "completed";
}

/**
 * @param {{ firstUserAt?: string | null, lastUserAt?: string | null, firstHumanAgentAt?: string | null, lastHumanAgentAt?: string | null }} slaMeta
 * @param {{ chatFirstResponseSeconds: number, chatAverageResponseSeconds: number }} settings
 * @param {number} nowMs
 * @param {string | null | undefined} [status] conversation status (resolved/closed → never breached)
 */
export function evaluateChatSlaBreach(slaMeta, settings, nowMs = Date.now(), status = null) {
  if (isClosedConversationStatus(status ?? slaMeta?.status)) {
    return { breached: false, firstBreached: false, avgBreached: false, reasons: [] };
  }

  const firstUser = parseIso(slaMeta?.firstUserAt);
  const lastUser = parseIso(slaMeta?.lastUserAt);
  const firstAgent = parseIso(slaMeta?.firstHumanAgentAt);
  const lastAgent = parseIso(slaMeta?.lastHumanAgentAt);

  const firstMs = settings.chatFirstResponseSeconds * 1000;
  const avgMs = settings.chatAverageResponseSeconds * 1000;

  let firstBreached = false;
  let avgBreached = false;

  // First-response breach applies only while waiting for the first human reply.
  if (firstUser != null && firstAgent == null) {
    firstBreached = nowMs - firstUser > firstMs;
  }

  if (lastUser != null) {
    const lastIsWaitingForAgent =
      lastAgent == null || (lastAgent != null && lastUser > lastAgent);
    if (lastIsWaitingForAgent) {
      avgBreached = nowMs - lastUser > avgMs;
    }
  }

  const breached = firstBreached || avgBreached;
  const reasons = [];
  if (firstBreached) reasons.push("First response SLA");
  if (avgBreached) reasons.push("Response time SLA");

  return { breached, firstBreached, avgBreached, reasons };
}

/**
 * @param {Record<string, unknown>} ticket
 * @param {{ ticketFirstResponseSeconds: number, ticketAverageResponseSeconds: number }} settings
 * @param {number} nowMs
 */
export function evaluateTicketSlaBreach(ticket, settings, nowMs = Date.now()) {
  const status = String(ticket?.status ?? "open").toLowerCase();
  if (status === "resolved" || status === "closed" || status === "completed") {
    return { breached: false, firstBreached: false, avgBreached: false, reasons: [] };
  }

  const created = parseIso(/** @type {string} */ (ticket?.createdAt));
  const firstAgent = parseIso(/** @type {string | undefined} */ (ticket?.firstAgentReplyAt));
  const lastAgent = parseIso(
    /** @type {string | undefined} */ (ticket?.lastAgentReplyAt ?? ticket?.firstAgentReplyAt)
  );
  const lastRequester = parseIso(
    /** @type {string | undefined} */ (ticket?.lastRequesterActivityAt ?? ticket?.createdAt)
  );

  const firstMs = settings.ticketFirstResponseSeconds * 1000;
  const avgMs = settings.ticketAverageResponseSeconds * 1000;

  let firstBreached = false;
  let avgBreached = false;

  if (created != null && firstAgent == null) {
    firstBreached = nowMs - created > firstMs;
  }

  if (lastRequester != null) {
    const waitingForAgent = lastAgent == null || lastRequester > lastAgent;
    if (waitingForAgent) {
      avgBreached = nowMs - lastRequester > avgMs;
    }
  }

  const breached = firstBreached || avgBreached;
  const reasons = [];
  if (firstBreached) reasons.push("First response SLA");
  if (avgBreached) reasons.push("Response time SLA");

  return { breached, firstBreached, avgBreached, reasons };
}
